import { createAction } from 'redux-starter-kit';
import { requestStart, requestFailed, requestSucceded } from '../../../domain/requests/RequestActions';
import { putDamageTypes, putDamageType, putDamageTypeReferences } from '../../../domain/damageTypes/damageTypeActions';
import { get, post } from '../../../../utility/via-fetch';
import { addMessage } from '../../application/messageUiActions';


/**
 * An action that signifies a damage type is being currently edited.
 * @example dispatch(editDamageType(damageTypeCode))
 */
export const editDamageType = createAction('EDIT_DAMAGE_TYPE');

/**
 * An action that signifies no damage type is being edited.
 * @example dispatch(cancelEditDamageType())
 */
export const cancelEditDamageType = createAction('CANCEL_EDIT_DAMAGE_TYPE');

/**
 * An action that signifies that a fetch to add a damage type has been
 * made in the Damage Type Configuration screen. This should pass through the request id.
 * @example dispatch(fetchDamageType(requestId))
 */
export const fetchAddDamageType = createAction('FETCH_CREATE_DAMAGE_TYPE');

/**
 * An action that signifies that a fetch to update a damage type
 * has been made in the Damage Type Configuration screen. This should pass through the request id.
 * @example dispatch(fetchUpdateDamageTypeId(requestId));
 */
export const fetchUpdateDamageType = createAction('FETCH_UPDATE_DAMAGE_TYPE');

export const addPublishDamageType = createAction('ADD_PENDING_PUBLISH_DAMAGE_TYPE');

export const removePublishDamageType = createAction('REMOVE_PENDING_PUBLISH_DAMAGE_TYPE');

/**
 * An action that signifies a fetch request to load damage types has been made.
 * @example dispatch(fetchDamageTypes(requestId))
 */
export const fetchDamageTypes = createAction('FETCH_DAMAGE_TYPES');

export function requestDamageTypes() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchDamageTypes.toString()));
    dispatch(fetchDamageTypes(requestId));
    try {
      const damageTypes = await get('/api/damage-types/');

      // Normalise data. Store the data in the domain, and the ids in the request response.
      dispatch(putDamageTypes(damageTypes));
      dispatch(requestSucceded(requestId, damageTypes.map(type => type.damageTypeCode)));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies a fetch request has been made that loads reference areas of a damage type.
 * @example dispatch(fetchDamageTypeReferences(requestId))
 */
export const fetchDamageTypeReferences = createAction('FETCH_DAMAGE_TYPE_REFERENCES');

export function requestForDamageTypeReferences(type) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchDamageTypeReferences.toString()));
    dispatch(fetchDamageTypeReferences(requestId));
    try {
      const damageTypeReferences = await get(`/api/damage-types/${type}/damage-areas/`);

      dispatch(putDamageTypeReferences(damageTypeReferences));
      dispatch(requestSucceded(requestId, damageTypeReferences.map(damageTypeRefs => damageTypeRefs.damageAreaDescription)));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

export function requestPublishDamageType(type) {
  const toggledType = { ...type, active: !type.active };

  return async (dispatch) => {
    const requestId = dispatch(requestStart(addPublishDamageType.toString()));
    dispatch(addPublishDamageType(toggledType.damageTypeCode));
    try {
      await post(`/api/damage-types/${type.damageTypeCode}`, {}, toggledType);
      dispatch(putDamageType(toggledType));
      dispatch(addMessage('success', 'itemStateUpdated', { item: type.damageTypeDescription }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
    dispatch(removePublishDamageType(toggledType.damageTypeCode));
  };
}

/**
 * An action for creating a damage type on the server.
 * @param {object} type A damage type to be created.
 * @example dispatch(requestDamageType(type));
 */
export function requestAddDamageType(type) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAddDamageType.toString()));
    dispatch(fetchAddDamageType(requestId));
    try {
      // Construct our new type object
      const createdType = {
        ...type,
      };

      // Create a new damage type with damage types.
      createdType.damageTypeCode = await post('/api/damage-types', {}, type);
      dispatch(putDamageType(createdType));
      dispatch(cancelEditDamageType());
      dispatch(requestSucceded(requestId, createdType.damageTypeCode));
      dispatch(addMessage('success', 'itemSaved', { item: createdType.damageTypeDescription }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Updates the damage type with new data.
 * @param {object} type The damage type to be crreated.
 */
export function requestUpdateDamageType(type) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart());
    dispatch(fetchUpdateDamageType(requestId));
    try {
      const updatedType = {
        ...type,
      };

      await post(`/api/damage-types/${type.damageTypeCode}`, {}, updatedType);
      dispatch(cancelEditDamageType());
      dispatch(putDamageType(updatedType));
      dispatch(requestSucceded(requestId, updatedType.damageTypeCode));
      dispatch(addMessage('success', 'itemUpdated', { item: updatedType.damageTypeDescription }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}
